/**
 * ProvinceFlags.jsx — Realistic mini province flags (16 × 11 inline SVG).
 * Shared across AccountPicker60s, QuestionFlow, and any future picker.
 */
import React from 'react';
import { Box } from '@mui/material';

/* ── Flag SVGs ── */
export const FLAG_DATA = {
  /* Ontario — Red Ensign: red field, Union Jack canton, Ontario shield in fly */
  ON: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#C8102E" />
      {/* Union Jack canton */}
      <rect width="7" height="5.5" fill="#012169" />
      <path d="M0 0L7 5.5M7 0L0 5.5" stroke="#fff" strokeWidth="0.8" />
      <path d="M3.5 0V5.5M0 2.75H7" stroke="#fff" strokeWidth="1.5" />
      <path d="M3.5 0V5.5M0 2.75H7" stroke="#CF142B" strokeWidth="0.6" />
      {/* Ontario arms — white/red cross top, green bottom */}
      <rect x="9" y="2.5" width="5.5" height="6" rx="0.8" fill="#006633" />
      <rect x="9" y="2.5" width="5.5" height="3" fill="#fff" rx="0.8" />
      <path d="M11.75 2.5V5.5M9 4H14.5" stroke="#C8102E" strokeWidth="0.5" />
    </svg>
  ),

  /* Quebec — Fleurdelisé: blue field, white cross, 4 fleur-de-lis */
  QC: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#003DA5" />
      <rect x="6" width="4" height="11" fill="#fff" />
      <rect y="3.5" width="16" height="4" fill="#fff" />
      {/* Fleur-de-lis (simplified) in each quadrant */}
      <path d="M3 1.2L2.4 2.6H3.6Z" fill="#fff" />
      <path d="M3 0.5V1.2" stroke="#fff" strokeWidth="0.4" />
      <path d="M13 1.2L12.4 2.6H13.6Z" fill="#fff" />
      <path d="M13 0.5V1.2" stroke="#fff" strokeWidth="0.4" />
      <path d="M3 8.4L2.4 9.8H3.6Z" fill="#fff" />
      <path d="M3 7.7V8.4" stroke="#fff" strokeWidth="0.4" />
      <path d="M13 8.4L12.4 9.8H13.6Z" fill="#fff" />
      <path d="M13 7.7V8.4" stroke="#fff" strokeWidth="0.4" />
    </svg>
  ),

  /* British Columbia — Union Jack top, setting sun, blue/white wavy stripes */
  BC: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      {/* Union Jack fills top portion */}
      <rect width="16" height="5" fill="#012169" />
      <path d="M0 0L16 5M16 0L0 5" stroke="#fff" strokeWidth="0.8" />
      <path d="M8 0V5M0 2.5H16" stroke="#fff" strokeWidth="1.5" />
      <path d="M8 0V5M0 2.5H16" stroke="#CF142B" strokeWidth="0.6" />
      {/* Crown hint */}
      <circle cx="8" cy="4" r="0.8" fill="#FFD700" />
      {/* Bottom — white bg, blue wavy stripes, setting sun */}
      <rect y="5" width="16" height="6" fill="#fff" />
      <circle cx="8" cy="5.5" r="1.6" fill="#FFD700" />
      <path d="M0 7.5Q4 6.5 8 7.5Q12 8.5 16 7.5V9.5Q12 10.5 8 9.5Q4 8.5 0 9.5Z" fill="#012169" />
    </svg>
  ),

  /* Alberta — Ultramarine blue field, coat of arms shield */
  AB: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#0038A8" />
      {/* Shield */}
      <rect x="3.5" y="1" width="9" height="9" rx="1" fill="#FFD700" />
      {/* St George cross top */}
      <rect x="3.5" y="1" width="9" height="2.5" fill="#fff" />
      <path d="M8 1V3.5M3.5 2.25H12.5" stroke="#C8102E" strokeWidth="0.5" />
      {/* Mountains */}
      <path d="M3.5 5L5.5 3.5L8 5.5L10.5 3.5L12.5 5Z" fill="#5B8A56" />
      {/* Green foothills */}
      <rect x="3.5" y="5" width="9" height="2.5" fill="#228B22" />
      {/* Wheat/prairie */}
      <rect x="3.5" y="7.5" width="9" height="2.5" rx="1" fill="#DAA520" />
    </svg>
  ),

  /* Manitoba — Red Ensign: red field, Union Jack canton, bison on shield */
  MB: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#C8102E" />
      {/* Union Jack canton */}
      <rect width="7" height="5.5" fill="#012169" />
      <path d="M0 0L7 5.5M7 0L0 5.5" stroke="#fff" strokeWidth="0.8" />
      <path d="M3.5 0V5.5M0 2.75H7" stroke="#fff" strokeWidth="1.5" />
      <path d="M3.5 0V5.5M0 2.75H7" stroke="#CF142B" strokeWidth="0.6" />
      {/* Manitoba arms — bison on green/gold */}
      <rect x="9" y="2.5" width="5.5" height="6" rx="0.8" fill="#006633" />
      <ellipse cx="11.75" cy="5.8" rx="1.8" ry="1.2" fill="#8B4513" />
      <circle cx="10.5" cy="5.2" r="0.6" fill="#8B4513" />
    </svg>
  ),

  /* Saskatchewan — Green/gold horizontal, western red lily, wheat sheaf */
  SK: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="5.5" fill="#006847" />
      <rect y="5.5" width="16" height="5.5" fill="#FDD017" />
      {/* Coat of arms shield — left */}
      <rect x="2" y="1" width="4" height="3.5" rx="0.5" fill="#FFD700" />
      <path d="M4 1.5L3.2 3H4.8Z" fill="#C8102E" />
      {/* Western Red Lily — right */}
      <path d="M12 7L11.2 9H12.8Z" fill="#C8102E" />
      <path d="M12 9V10" stroke="#006847" strokeWidth="0.4" />
    </svg>
  ),

  /* Nova Scotia — Blue field, white saltire (St Andrew's cross), Scottish arms */
  NS: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#012169" />
      {/* White saltire */}
      <path d="M0 0L16 11M16 0L0 11" stroke="#fff" strokeWidth="2.5" />
      {/* Scottish royal arms — red lion on gold */}
      <rect x="5" y="2.5" width="6" height="6" rx="0.5" fill="#FFD700" />
      <path d="M7 4L8 3.5L9 4V6.5L8 7L7 6.5Z" fill="#C8102E" />
    </svg>
  ),

  /* New Brunswick — Gold lion on red (top), gold galley on blue (bottom) */
  NB: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      {/* Red top — gold lion */}
      <rect width="16" height="5" fill="#C8102E" />
      <rect x="4" y="0.5" width="8" height="4" rx="0.5" fill="#FFD700" />
      <path d="M7 1.5L8 1L9 1.5V3.5L8 4L7 3.5Z" fill="#C8102E" />
      {/* Blue bottom — gold galley */}
      <rect y="5" width="16" height="6" fill="#012169" />
      <path d="M5 9.5L11 9.5L12 10.5H4Z" fill="#FFD700" />
      <path d="M8 6.5V9.5" stroke="#FFD700" strokeWidth="0.6" />
      <path d="M6.5 7.5H9.5" stroke="#FFD700" strokeWidth="0.5" />
    </svg>
  ),

  /* Newfoundland & Labrador — Christopher Pratt geometric design */
  NL: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#fff" />
      {/* Blue shapes — left */}
      <path d="M0 0H5L7 5.5L5 11H0Z" fill="#012169" />
      {/* Red shapes — right */}
      <path d="M16 0H11L9 5.5L11 11H16Z" fill="#C8102E" />
      {/* Gold central sword/arrow */}
      <path d="M5 1.5L7 5.5L5 9.5L9 5.5Z" fill="#FFD700" />
      <path d="M11 1.5L9 5.5L11 9.5Z" fill="#FFD700" />
    </svg>
  ),

  /* PEI — Red field, white center panel, lion top, oak tree bottom */
  PE: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" fill="#C8102E" />
      {/* Wide white center */}
      <rect x="2" width="12" height="11" fill="#fff" />
      {/* Red border bars */}
      <rect width="2" height="11" fill="#C8102E" />
      <rect x="14" width="2" height="11" fill="#C8102E" />
      {/* Gold lion top */}
      <rect x="4" y="0.5" width="8" height="3" fill="#C8102E" />
      <rect x="5" y="1" width="6" height="2" fill="#FFD700" />
      {/* Oak tree */}
      <circle cx="8" cy="6.5" r="1.8" fill="#006633" />
      <rect x="7.6" y="8" width="0.8" height="1.5" fill="#8B4513" />
      {/* Small saplings */}
      <circle cx="5.5" cy="8" r="0.8" fill="#228B22" />
      <circle cx="10.5" cy="8" r="0.8" fill="#228B22" />
    </svg>
  ),

  /* Northwest Territories — Blue/white/blue, NWT shield */
  NT: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="4.5" height="11" fill="#012169" />
      <rect x="4.5" width="7" height="11" fill="#fff" />
      <rect x="11.5" width="4.5" height="11" fill="#012169" />
      {/* NWT shield */}
      <rect x="5.5" y="1.5" width="5" height="6.5" rx="0.5" fill="#FFD700" />
      <rect x="5.5" y="1.5" width="5" height="3" fill="#012169" />
      <path d="M5.5 4.5H10.5" stroke="#fff" strokeWidth="0.6" />
      {/* White fox silhouette */}
      <ellipse cx="8" cy="7" rx="1.5" ry="0.8" fill="#fff" />
    </svg>
  ),

  /* Nunavut — Gold/white vertical, inuksuk, North Star */
  NU: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="7" height="11" fill="#FFD700" />
      <rect x="7" width="9" height="11" fill="#fff" />
      {/* Red inuksuk */}
      <rect x="6.8" y="2.5" width="2.4" height="0.8" rx="0.2" fill="#C8102E" />
      <rect x="7.4" y="3.3" width="1.2" height="0.8" fill="#C8102E" />
      <rect x="6.8" y="4.1" width="2.4" height="0.8" rx="0.2" fill="#C8102E" />
      <rect x="7.4" y="4.9" width="1.2" height="1.5" fill="#C8102E" />
      <rect x="6.8" y="6.4" width="0.8" height="1.2" fill="#C8102E" />
      <rect x="8.4" y="6.4" width="0.8" height="1.2" fill="#C8102E" />
      {/* Blue North Star */}
      <path d="M12.5 2.5L12.8 3.5L13.8 3.5L13 4.1L13.3 5.1L12.5 4.5L11.7 5.1L12 4.1L11.2 3.5L12.2 3.5Z" fill="#012169" />
    </svg>
  ),

  /* Yukon — Green/white/blue triband, coat of arms */
  YT: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="5" height="11" fill="#006847" />
      <rect x="5" width="6" height="11" fill="#fff" />
      <rect x="11" width="5" height="11" fill="#012169" />
      {/* Coat of arms — St George cross + malamute */}
      <rect x="6" y="1" width="4" height="4" rx="0.5" fill="#fff" />
      <path d="M8 1V5M6 3H10" stroke="#C8102E" strokeWidth="0.5" />
      {/* Malamute silhouette */}
      <path d="M7 6.5L7.5 6L8.5 6L9 6.5V8.5L8.5 9H7.5L7 8.5Z" fill="#333" />
    </svg>
  ),

  /* "No sé / no he decidido" — Grey placeholder */
  UNKNOWN: (
    <svg viewBox="0 0 16 11" width="16" height="11">
      <rect width="16" height="11" rx="1" fill="#C4C9D2" />
      <text x="8" y="8.5" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#fff">?</text>
    </svg>
  ),
};

/* ── ProvinceFlag component ── */
export const ProvinceFlag = ({ code }) => {
  const svg = FLAG_DATA[code];
  if (!svg) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width: 16,
        height: 11,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 0 0 0.5px rgba(0,0,0,0.1)',
        flexShrink: 0,
      }}
    >
      {svg}
    </Box>
  );
};
